"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"

const tabs = [
  { id: "hand" },
  { id: "foot" },
  { id: "garments" },
  { id: "respiratory" },
  { id: "eye" },
  { id: "ear" },
  { id: "skull" },
  { id: "disposable" },
  { id: "fall" },
]

// Each tab has a list of content blocks: image and text
const content: Record<string, { items: { image: string; textKey: string }[] }> = {
  hand: {
    items: [
      { image: "https://www.coverguard-safety.com/media/cms/en388_1.png", textKey: "hand.items.0" },
      { image: "https://www.coverguard-safety.com/media/cms/niveau_de_perf_en388.png", textKey: "hand.items.1" },
      { image: "https://www.coverguard-safety.com/media/cms/test_coupure.png", textKey: "hand.items.2" },
      { image: "https://www.coverguard-safety.com/media/cms/correspondance_ansi_iso.png", textKey: "hand.items.3" },
    ],
  },
  foot: {
    items: [
      { image: "https://www.coverguard-safety.com/media/cms/chaussure_classe.png", textKey: "foot.items.0" },
      { image: "https://www.coverguard-safety.com/media/cms/shoe_glissement.png", textKey: "foot.items.1" },
      { image: "https://www.coverguard-safety.com/media/cms/shoes_normadd.png", textKey: "foot.items.2" },
    ],
  },
  garments: {
    items: [
      { image: "https://www.coverguard-safety.com/media/EN14058_1_1.png", textKey: "garments.items.0" },
      { image: "https://www.coverguard-safety.com/media/EN14058_Tableau1.png", textKey: "garments.items.1" },
      { image: "https://www.coverguard-safety.com/media/webp_image/EN342_EN.webp", textKey: "garments.items.2" },
      { image: "https://www.coverguard-safety.com/media/webp_image/EN343_EN.webp", textKey: "garments.items.3" },
      { image: "https://www.coverguard-safety.com/media/cms/en20471.png", textKey: "garments.items.4" },
    ],
  },
  respiratory: {
    items: [
      { image: "https://www.coverguard-safety.com/media/tableau_respi.PNG", textKey: "respiratory.items.0" },
      { image: "https://www.coverguard-safety.com/media/normes_respi.PNG", textKey: "respiratory.items.1" },
      { image: "https://www.coverguard-safety.com/media/webp_image/niveau_perf_filtres.webp", textKey: "respiratory.items.2" },
    ],
  },
  eye: {
    items: [
      { image: "https://www.coverguard-safety.com/media/normes_lunettes.PNG", textKey: "eye.items.0" },
      { image: "https://www.coverguard-safety.com/media/verres_polarisants.PNG", textKey: "eye.items.1" },
      { image: "https://www.coverguard-safety.com/media/filtres.PNG", textKey: "eye.items.2" },
    ],
  },
  ear: {
    items: [
      { image: "https://www.coverguard-safety.com/media/tableau_normes_auditifs.PNG", textKey: "ear.items.0" },
      { image: "https://www.coverguard-safety.com/media/webp_image/esd-gloves.webp", textKey: "ear.items.1" },
    ],
  },
  skull: {
    items: [
      { image: "https://www.coverguard-safety.com/media/cms/en13594.png", textKey: "skull.items.0" },
      { image: "https://www.coverguard-safety.com/media/webp_image/esd-gloves.webp", textKey: "skull.items.1" },
    ],
  },
  disposable: {
    items: [
      { image: "https://www.coverguard-safety.com/media/cms/en374.png", textKey: "disposable.items.0" },
      { image: "https://www.coverguard-safety.com/media/CE_en374.PNG", textKey: "disposable.items.1" },
    ],
  },
  fall: {
    items: [
      { image: "https://www.coverguard-safety.com/media/cms/en20471.png", textKey: "fall.items.0" },
      { image: "https://www.coverguard-safety.com/media/webp_image/en20471_2.webp", textKey: "fall.items.1" },
    ],
  },
}

export default function CertificationPageClient() {
  const t = useTranslations("Certification")
  const [activeTab, setActiveTab] = useState<string>(tabs[0].id)
  const items = content[activeTab].items

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">{t("pageTitle")}</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t("pageDescription")}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Navigation Tabs */}
          <nav className="lg:w-1/4 space-y-2">
            <h2 className="text-xl font-semibold mb-4">{t("categories")}</h2>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 ${
                  activeTab === tab.id 
                    ? "bg-primary text-white shadow-md" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                {t(`tabs.${tab.id}`)}
              </button>
            ))}
          </nav>

          {/* Content Section */}
          <section className="lg:w-3/4 space-y-8">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-3xl font-semibold mb-6 text-primary">
                {t(`tabs.${activeTab}`)}
              </h2>
              
              <div className="space-y-8">
                {items.map((block, idx) => (
                  <div key={idx} className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <img
                        src={block.image}
                        alt={`${t(`tabs.${activeTab}`)} стандарт ${idx + 1}`}
                        className="rounded-lg shadow-md max-w-full mx-auto"
                        loading="lazy"
                      />
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 leading-relaxed">
                        {t(block.textKey)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Additional SEO content */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <p className="mb-2">
                    <strong>СИЗ сертификация</strong> - важный процесс обеспечения безопасности на рабочем месте. 
                    Все средства индивидуальной защиты должны соответствовать европейским стандартам EN.
                  </p>
                  <p>
                    Наша компания предлагает только сертифицированные СИЗ с полным комплектом документации 
                    и соответствием всем требованиям безопасности труда.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
